import { useRouter } from "@tanstack/react-router";
import { WifiOff } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import type { OfflineRouteStance } from "@/lib/offline/routeGuard";

/**
 * Inline "not available offline" panel, rendered in the matched route's slot
 * (via the router's defaultErrorComponent) so the nav/shell stays intact.
 *
 * - "preparable"  → dataset copy: prepare this data in the Offline Center.
 * - "online-only" → screen copy: this feature requires a connection. Never
 *   reuse the datasets copy here — pointing users at the Offline Center for
 *   features that can never be prepared is misleading.
 */
export function OfflineUnavailable({
	variant = "online-only",
}: {
	variant?: Exclude<OfflineRouteStance, "ready">;
}) {
	const { t } = useTranslation();
	const router = useRouter();
	const online = useOnlineStatus();

	// A guarded route stays in error state after connectivity returns; the
	// router only re-runs beforeLoad on invalidation or re-navigation. Watch
	// the reachability signal and invalidate so the screen recovers in place.
	useEffect(() => {
		if (online) router.invalidate();
	}, [online, router]);

	const copyKey =
		variant === "preparable"
			? "offline.datasets.unavailable"
			: "offline.screen.unavailable";

	return (
		<div className="flex items-center justify-center py-16 text-center">
			<div>
				<WifiOff className="mx-auto h-8 w-8 text-base-content/40" />
				<p className="font-medium text-base-content/70 mt-3">
					{t(`${copyKey}.title`)}
				</p>
				<p className="text-sm text-base-content/50 mt-1">
					{t(`${copyKey}.description`)}
				</p>
				<Button
					variant="outline"
					size="sm"
					className="mt-4"
					onClick={() => router.invalidate()}
				>
					{t("common.retry")}
				</Button>
			</div>
		</div>
	);
}
